// import PaginatedList from '@theme/paginated-list';

// /**
//  * A custom element that renders a paginated blog posts list
//  */
// export default class BlogPostsList extends PaginatedList {}

// if (!customElements.get('blog-posts-list')) {
//   customElements.define('blog-posts-list', BlogPostsList);
// }
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a paginated blog posts list
 */
export default class BlogPostsList extends PaginatedList {
  currentLoadedPage = 1; // Track which page we're on
  
  connectedCallback() {
    // DON'T call super.connectedCallback() to disable infinite scroll
    // super.connectedCallback();
    
    // Check what page we're on from URL
    const urlParams = new URLSearchParams(window.location.search);
    const urlPage = parseInt(urlParams.get('page')) || 1;
    this.currentLoadedPage = urlPage;
    
    // Only initialize what we need
    this.handleResize = () => this.equalizeCardHeights();
    window.addEventListener('resize', this.handleResize);

    this.initObservers();
    this.equalizeCardHeights();
    
    // If we're on page > 1, load previous pages
    if (urlPage > 1) {
      setTimeout(() => {
        this.loadPreviousPages(urlPage);
      }, 100);
    }
    
    // Load More button functionality - initialize after a delay to ensure DOM is ready
    setTimeout(() => {
      this.setupLoadMoreButton();
    }, 100);
    
    // Set up global function with proper context binding
    window.blogLoadMore = () => {
      this.loadMore();
    };
  }

  // Load all pages from 1 to currentPage-1
  async loadPreviousPages(currentPage) {
    const grid = this.querySelector('[ref="grid"]');
    if (!grid) return;
    
    // Load pages in order from 1 to currentPage-1
    for (let pageNum = 1; pageNum < currentPage; pageNum++) {
      await this.loadSpecificPage(pageNum, true); // true = prepend instead of append
    }
  }
  
  // Load a specific page and add it to the grid
  async loadSpecificPage(pageNumber, prepend = false) {
    const grid = this.querySelector('[ref="grid"]');
    if (!grid) return;
    
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('page', pageNumber.toString());
      
      const response = await fetch(url.toString());
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const fetchedGrid = doc.querySelector('[ref="grid"]');
      
      if (!fetchedGrid) return;
      
      const newCards = fetchedGrid.querySelectorAll('[ref="cards[]"]');
      
      // Get existing card IDs to avoid duplicates
      const existingCards = grid.querySelectorAll('[ref="cards[]"]');
      const existingCardIds = new Set();
      existingCards.forEach(card => {
        const articleLink = card.querySelector('a[href*="/blogs/"]');
        if (articleLink) existingCardIds.add(articleLink.href);
      });
      
      const cardsToAdd = [];
      newCards.forEach((card) => {
        const articleLink = card.querySelector('a[href*="/blogs/"]');
        const cardId = articleLink ? articleLink.href : null;
        
        if (cardId && !existingCardIds.has(cardId)) {
          const clonedCard = card.cloneNode(true);
          clonedCard.setAttribute('data-page', pageNumber.toString());
          cardsToAdd.push(clonedCard);
          existingCardIds.add(cardId);
        }
      });
      
      // Add cards (prepend for previous pages, append for next pages)
      if (prepend) {
        cardsToAdd.reverse().forEach(card => {
          grid.insertBefore(card, grid.firstChild);
        });
      } else {
        cardsToAdd.forEach(card => {
          grid.appendChild(card);
        });
      }
    } catch (error) {
      console.error(`Error loading page ${pageNumber}:`, error);
    }
  }

  setupLoadMoreButton() {
    this.loadMoreButton = this.querySelector('#loadMoreBtn');
    
    if (this.loadMoreButton) {
      this.handleLoadMoreClick = (e) => {
        e.preventDefault();
        this.loadMore();
      };
      this.loadMoreButton.addEventListener('click', this.handleLoadMoreClick);
      this.updateButton();
    } else {
      // Try again after a longer delay
      setTimeout(() => {
        this.loadMoreButton = this.querySelector('#loadMoreBtn');
        if (this.loadMoreButton) {
          this.handleLoadMoreClick = (e) => {
            e.preventDefault();
            this.loadMore();
          };
          this.loadMoreButton.addEventListener('click', this.handleLoadMoreClick);
          this.updateButton();
        }
      }, 500);
    }
  }

  // triggers the next page to load
  async loadMore() {
    // Find button dynamically in case it wasn't set yet
    const button = this.loadMoreButton || this.querySelector('#loadMoreBtn');
    
    if (!button) return;
    
    // Disable button while loading
    button.disabled = true;
    const showMoreText = Theme.translations?.show_more || 'Pokaż więcej';
    button.textContent = Theme.translations.loading;
    
    try {
      const grid = this.querySelector('[ref="grid"]');
      
      if (!grid) {
        button.disabled = false;
        button.textContent = showMoreText;
        return;
      }
      
      const lastPage = parseInt(grid.getAttribute('last-page')) || 1;
      const nextPageNumber = this.currentLoadedPage + 1;
      
      if (nextPageNumber > lastPage) {
        button.style.display = 'none';
        return;
      }
      
      // Build URL for next page
      const url = new URL(window.location.href);
      url.searchParams.set('page', nextPageNumber.toString());
      url.hash = '';
      
      // Fetch next page HTML
      const response = await fetch(url.toString());
      const html = await response.text();
      
      // Parse the HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Get new cards from the fetched page - look for grid first
      const fetchedGrid = doc.querySelector('[ref="grid"]');
      
      if (!fetchedGrid) {
        button.disabled = false;
        button.textContent = showMoreText;
        return;
      }
      
      const newCards = fetchedGrid.querySelectorAll('[ref="cards[]"]');
      
      if (newCards.length > 0) {
        // Check if we already have these cards (by comparing some unique identifier)
        const existingCards = grid.querySelectorAll('[ref="cards[]"]');
        const existingCardIds = new Set();
        existingCards.forEach(card => {
          const articleLink = card.querySelector('a[href*="/blogs/"]');
          if (articleLink) {
            existingCardIds.add(articleLink.href);
          }
        });
        
        // Only append cards that we don't already have
        newCards.forEach((card) => {
          const articleLink = card.querySelector('a[href*="/blogs/"]');
          const cardId = articleLink ? articleLink.href : null;
          
          if (cardId && !existingCardIds.has(cardId)) {
            const clonedCard = card.cloneNode(true);
            clonedCard.setAttribute('data-page', nextPageNumber.toString());
            grid.appendChild(clonedCard);
            existingCardIds.add(cardId);
          }
        });
        
        // Update our page tracker
        this.currentLoadedPage = nextPageNumber;
        
        // Update URL without page reload
        if (nextPageNumber > 1) {
          history.pushState('', '', url.toString());
        }
        
        // Equalize heights after adding new cards
        await this.equalizeCardHeights();
      }
      
      // Check if there are more pages
      if (nextPageNumber >= lastPage) {
        button.style.display = 'none';
      } else {
        button.disabled = false;
        button.textContent = showMoreText;
      }
      
    } catch (error) {
      console.error('Error loading more posts:', error);
      button.disabled = false;
      button.textContent = showMoreText;
    }
  }

  // hides the button when there are no more pages
  updateButton() {
    if (!this.loadMoreButton) return;
    
    const grid = this.querySelector('[ref="grid"]');
    if (!grid) return;
    
    const lastPage = parseInt(grid.getAttribute('last-page')) || 1;
    const currentPage = this.currentLoadedPage;
    
    if (currentPage >= lastPage) {
      this.loadMoreButton.style.display = 'none';
    } else {
      this.loadMoreButton.style.display = '';
    }
  }

  disconnectedCallback() {
    if (super.disconnectedCallback) {
      super.disconnectedCallback();
    }

    if (this.loadMoreButton && this.handleLoadMoreClick) {
      this.loadMoreButton.removeEventListener('click', this.handleLoadMoreClick);
    }

    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize);
    }

    if (this.cardsResizeObserver) {
      this.cardsResizeObserver.disconnect();
    }

    if (this.cardsMutationObserver) {
      this.cardsMutationObserver.disconnect();
    }
  }
  
  initObservers() {
    const cardsContainer = this.querySelector('.blog-posts-container');
    if (!cardsContainer) {
      return;
    }

    if (typeof ResizeObserver !== 'undefined') {
      this.cardsResizeObserver = new ResizeObserver(() => {
        this.equalizeCardHeights();
      });
      this.cardsResizeObserver.observe(cardsContainer);
    }

    this.cardsMutationObserver = new MutationObserver(() => {
      this.equalizeCardHeights();
    });
    this.cardsMutationObserver.observe(cardsContainer, {
      childList: true,
      subtree: true,
    });
  }

  async equalizeCardHeights() {
    if (!this.isConnected) {
      return;
    }

    const cards = Array.from(
      this.querySelectorAll('.blog-post-item:not(:first-child) .blog-post-card'),
    );

    if (!cards.length) {
      return;
    }

    cards.forEach((card) => {
      card.style.removeProperty('min-height');
    });

    await this.waitForImages(cards);

    const maxHeight = cards.reduce((max, card) => {
      return Math.max(max, card.offsetHeight);
    }, 0);

    if (!maxHeight) {
      return;
    }

    cards.forEach((card) => {
      card.style.minHeight = `${maxHeight}px`;
    });
  }

  waitForImages(cards) {
    const images = [];

    cards.forEach((card) => {
      card.querySelectorAll('img').forEach((img) => {
        images.push(img);
      });
    });

    const pendingImages = images.filter((image) => !image.complete);

    if (!pendingImages.length) {
      return Promise.resolve();
    }

    return Promise.all(
      pendingImages.map(
        (image) =>
          new Promise((resolve) => {
            const done = () => resolve();
            image.addEventListener('load', done, { once: true });
            image.addEventListener('error', done, { once: true });
          }),
      ),
    );
  }
}

if (!customElements.get('blog-posts-list')) {
  customElements.define('blog-posts-list', BlogPostsList);
}

